/**
 * Guest-registration filing: the obligation the filing engine files a
 * property's guests by, the filing's lifecycle vocabulary, and how a guest's
 * filings fold into one status.
 *
 * Canonical source of truth for the monorepo: the `*Values` arrays feed
 * `pgEnum()` in `packages/db/src/schema/`.
 */

import { z } from "zod";

// Execution bucket

/**
 * - `a`: a single official national machine-to-machine API covers the whole
 *   country (Spain SES.HOSPEDAJES, Italy Alloggiati Web, Croatia eVisitor,
 *   Portugal SIBA, Slovenia eTurizem, Czechia UbyPort). A hard-coded connector
 *   makes the call.
 * - `b`: nothing is submitted — the host fills in a form and keeps it on file,
 *   shown to police only on request (France fiche de police, Germany
 *   Meldeschein). We generate, e-sign and retain it; there is no endpoint.
 * - `c`: fragmented (per-municipality / per-vendor) or a real API behind a
 *   certification gauntlet not worth the market (Austria, Netherlands, Greece,
 *   Hungary). Assisted-manual now, a vendor bridge later.
 */
export const registrationBucketValues = ["a", "b", "c"] as const;
export const RegistrationBucketSchema = z.enum(registrationBucketValues);

/**
 * Who the obligation covers. Some regimes register every guest; others only
 * foreign nationals (own-country nationals, and often EU/EEA citizens, are
 * exempt).
 */
export const registrationReportScopeValues = [
  "all_guests",
  "foreign_guests_only",
] as const;
export const RegistrationReportScopeSchema = z.enum(
  registrationReportScopeValues
);
export type RegistrationReportScope = z.infer<
  typeof RegistrationReportScopeSchema
>;

// The retired registration brief's enum values: its tables still declare them
// until the contract step drops those (packages/db/src/schema/registration.ts).
export const registrationBriefStatusValues = [
  "empty",
  "ai_generated",
  "host_edited",
  "generating",
] as const;
export const registrationBriefOriginValues = ["ai", "host"] as const;

// Guest-registration filing engine
//
// A "filing" is one government guest-registration obligation chased to closure:
// one guest, one reservation, one direction (check-in or check-out), one system.
// The catalog's statute (`catalogFilingObligation`) supplies the obligation;
// the per-country connector makes the actual wire call.

/** Check-in vs check-out. Several regimes (Slovenia, Croatia, Turkey) require a
 * separate report when the guest checks out, so a guest can accrue two filings
 * for one stay. Regulators call these arrival/departure reports; the product
 * speaks check-in/check-out, and each connector translates at the wire. */
export const guestFilingKindValues = ["check_in", "check_out"] as const;
export const GuestFilingKindSchema = z.enum(guestFilingKindValues);
export type GuestFilingKind = z.infer<typeof GuestFilingKindSchema>;

/**
 * Status machine:
 *   pending           created; submits at its due time (the check-in/check-out
 *                     moment) or earlier via an event trigger once due
 *   submitting        a worker has claimed it and the connector call is in flight
 *   submitted         the authority accepted it; confirmation is still pending
 *   confirmed         the authority confirmed; receipt stored where one exists
 *   failed            a transient error; a retry is scheduled (nextRetryAt)
 *   needs_host_action a hard error, exhausted retries, a blown deadline, or a
 *                     report the host owes because they filed the stay's other
 *                     direction themselves
 *   canceled          the obligation no longer applies, or the host filed it
 *                     with the authority themselves (`filedManuallyAt`)
 */
export const guestFilingStatusValues = [
  "pending",
  "submitting",
  "submitted",
  "confirmed",
  "failed",
  "needs_host_action",
  "canceled",
] as const;
export const GuestFilingStatusSchema = z.enum(guestFilingStatusValues);
export type GuestFilingStatus = z.infer<typeof GuestFilingStatusSchema>;

/**
 * The statuses at which an authority has accepted a filing: the one definition
 * of "accepted" every reader counts by. `submitted` belongs here because it is
 * already the authority's acceptance, and a connector whose authority never
 * returns a receipt may never move a filing past it.
 */
export const ACCEPTED_GUEST_FILING_STATUSES = [
  "submitted",
  "confirmed",
] as const satisfies readonly GuestFilingStatus[];

/** The statuses at which a filing's record is reaching or has reached a register. */
export const FILED_GUEST_FILING_STATUSES = [
  "submitting",
  ...ACCEPTED_GUEST_FILING_STATUSES,
] as const satisfies readonly GuestFilingStatus[];

/** Append-only audit event types. One row per meaningful transition, kept
 * server-side for the host's own records and for proving compliance on a
 * police inspection. */
export const guestFilingEventTypeValues = [
  "created",
  "attempt_started",
  "submitted",
  "confirmed",
  "transient_error",
  "permanent_error",
  "retry_scheduled",
  "escalated",
  "deadline_missed",
  "receipt_stored",
  "canceled",
  "host_retry_requested",
  // A host took an accepted filing back out of the authority's register. Its
  // own type rather than a `canceled`: canceled means Vidimo will not file it
  // (the stay was voided, or the host filed it themselves, which its message
  // says), this means Vidimo filed it and it was then undone at the register.
  "withdrawn",
] as const;
export const GuestFilingEventTypeSchema = z.enum(guestFilingEventTypeValues);
export type GuestFilingEventType = z.infer<typeof GuestFilingEventTypeSchema>;

/** Host-facing error categories. The raw provider response lives in the event
 * log; this is the bucket the staff app renders an action from. */
export const guestFilingErrorCodeValues = [
  "transient",
  "auth",
  "validation",
  "missing_data",
  "no_connector",
  "unknown",
  // The host filed this guest-stay themselves, so Vidimo must not file this
  // direction under its own guest-book number: the host files it too.
  "filed_by_host",
  // The guest's record is out under another AJPES facility (RNO) than the
  // property is connected to now, or under one nobody recorded: only the host
  // can finish or cancel it there.
  "other_facility",
  // A submission left and no answer said which of its guests the register
  // took, so this one may already be on file: the host checks first.
  "unattributed",
] as const;
export const GuestFilingErrorCodeSchema = z.enum(guestFilingErrorCodeValues);
export type GuestFilingErrorCode = z.infer<typeof GuestFilingErrorCodeSchema>;

/**
 * Which side of the wire produced the sentence stored on a failed filing.
 *
 * `authority` means the office answered and this is their verdict, so the host
 * card may quote it as theirs. `local` means we stopped before or without a
 * verdict — an incomplete capture, a credential we never sent, a certificate
 * that would not load — and the sentence is our own prose.
 *
 * The distinction does not follow the error CODE: `auth` covers both a
 * credential the authority rejected and one we never had, and only the code
 * that raised the outcome knows which happened.
 */
export const guestFilingErrorSourceValues = ["local", "authority"] as const;
export const GuestFilingErrorSourceSchema = z.enum(
  guestFilingErrorSourceValues
);
export type GuestFilingErrorSource = z.infer<
  typeof GuestFilingErrorSourceSchema
>;

/**
 * Server-only payload stored on each audit event: the sanitized request sent to
 * the authority and its response, for the host's records and inspection proof.
 * Never synced to clients. Kept loose (`unknown`) because each connector's wire
 * shape differs; the connector is responsible for stripping secrets first.
 */
export interface GuestFilingEventDetail {
  errorCode?: GuestFilingErrorCode;
  providerRef?: string;
  request?: unknown;
  response?: unknown;
  /**
   * The authority's reference for a WITHDRAWAL, which is a different act from
   * the filing it cancels. Kept beside `providerRef` rather than overwriting
   * it: the audit log has to be able to say both what was filed and what took
   * it back out.
   */
  withdrawalRef?: string;
}

/**
 * What the filing engine files a property's guests by: the catalog's rules
 * (`catalogFilingObligation`). Snapshotted onto each filing at enqueue time.
 */
export const registrationObligationSchema = z.object({
  bucket: RegistrationBucketSchema,
  /** Hours after arrival the report is legally due (12 in Slovenia, up to 72). */
  deadlineHours: z.number().int().positive(),
  /** Hours after departure that report is due; defaults to deadlineHours. */
  departureDeadlineHours: z.number().int().positive().optional(),
  /** Whether a separate departure report is required (Slovenia, Croatia, ...). */
  departureFilingRequired: z.boolean().default(false),
  /**
   * Connector-registry key. For bucket A this MUST equal the `RegistrationSystem`
   * value from `registration-systems.ts` (the credential vault uses the same key).
   * Bucket B/C are free-form (no connector, no vault entry).
   */
  system: z.string().min(1),
  whoMustReport: RegistrationReportScopeSchema.default("all_guests"),
});
export type RegistrationObligation = z.infer<
  typeof registrationObligationSchema
>;

// Per-guest registration summary

/**
 * The registration state of a single guest on a stay. One state per guest,
 * folding that guest's check-in and check-out filings into a single governing
 * status.
 */
export type GuestRegistrationState =
  | "registered"
  | "submitted"
  | "filing"
  | "scheduled"
  // Pending with no due time: at an in-person property, nobody has checked
  // this guest in, so nothing files them yet (ADR-0146).
  | "awaiting_check_in"
  | "action_needed"
  // The host told us they filed this guest with the authority themselves.
  // Not a status: it is `canceled` with a `filedManuallyAt`, because Vidimo no
  // longer owes the filing and must never send it.
  | "filed_manually"
  // The host withdrew the filing from the register after the authority had
  // accepted it. Not a status: it is `confirmed` with a `withdrawnAt`, because
  // the record still exists at the authority carrying a cancelled validity.
  | "withdrawn";

/** The filing fields the summary reduction reads. */
export interface FilingLike {
  /** Null while the filing cannot fall due (ADR-0146). Required, so no
   *  caller reads a guest nobody checked in as scheduled by leaving it out. */
  dueAt: string | Date | null;
  /** Set once a host has said they filed this guest themselves. */
  filedManuallyAt?: string | Date | null;
  kind: GuestFilingKind;
  lastErrorCode: GuestFilingErrorCode | null;
  status: GuestFilingStatus;
  /** Set once a host has withdrawn this filing from the register. */
  withdrawnAt?: string | Date | null;
}

export interface GuestRegistrationSummary<T extends FilingLike = FilingLike> {
  /** The filing a host action targets (needs_host_action or exhausted failed). */
  actionable: T | null;
  /** Error category on the actionable filing, chooses which action to offer. */
  errorCode: GuestFilingErrorCode | null;
  /**
   * The filing whose record is in the authority's register: one they have
   * accepted, whether or not the host has since withdrawn it. Null when
   * nothing has been accepted.
   *
   * Read off the GOVERNING filing for the same reason `actionable` is — the row
   * shows one state, and an action offered against a different filing than the
   * one on screen is how a host cancels the wrong record. Whether to OFFER a
   * withdrawal is `filed` together with `state !== "withdrawn"`, so the
   * decision cannot disagree with the state beside it.
   */
  filed: T | null;
  /** A check-out filing is queued for departure (the muted happy-path hint). */
  hasScheduledCheckout: boolean;
  state: GuestRegistrationState;
  /**
   * When the record was withdrawn, or null.
   *
   * Carried on the summary rather than read off `filed`, because the filing
   * that HOLDS the withdrawal is not always the governing one: an arrival
   * withdrawn beside a `submitted` departure makes the departure govern, and
   * its own `withdrawnAt` is null. Reading the date there showed the note with
   * no date at all, while the guide promises the row says when it happened.
   */
  withdrawnAt: Date | string | null;
}

/**
 * Attention rank: higher wins when a guest's two filings disagree. A pending
 * filing carries no attention on its own (it is simply scheduled), so it sits
 * below confirmed — a filed check-in must not be masked by a not-yet-due
 * check-out. `failed`/`needs_host_action` are the only states that let a
 * check-out override an otherwise-registered guest.
 */
const ATTENTION_RANK: Record<GuestFilingStatus, number> = {
  canceled: -1,
  confirmed: 1,
  failed: 4,
  needs_host_action: 5,
  pending: 0,
  submitted: 2,
  submitting: 3,
};

const STATE_BY_STATUS: Record<GuestFilingStatus, GuestRegistrationState> = {
  canceled: "registered",
  confirmed: "registered",
  failed: "filing",
  needs_host_action: "action_needed",
  pending: "scheduled",
  submitted: "submitted",
  submitting: "filing",
};

/**
 * A filing the host made themselves ranks as a registered one: the obligation
 * is met, and a pending departure beside it must not mask that.
 */
function attentionRank(filing: FilingLike): number {
  return filing.filedManuallyAt
    ? ATTENTION_RANK.confirmed
    : ATTENTION_RANK[filing.status];
}

/** A filing voided with its stay: never owed, so it answers for nothing. */
export function isVoidedFiling(
  filing: Pick<FilingLike, "filedManuallyAt" | "status">
): boolean {
  return filing.status === "canceled" && !filing.filedManuallyAt;
}

/** Pick the filing that governs the row, by attention rank (check-in breaks ties). */
export function governingFiling<T extends FilingLike>(filings: T[]): T | null {
  let best: T | null = null;
  for (const filing of filings) {
    if (isVoidedFiling(filing)) {
      continue;
    }
    if (
      !best ||
      attentionRank(filing) > attentionRank(best) ||
      // Same rank: prefer the check-in filing so it drives the primary label.
      (attentionRank(filing) === attentionRank(best) &&
        filing.kind === "check_in")
    ) {
      best = filing;
    }
  }
  return best;
}

/**
 * A filing Vidimo cannot send yet because its guest is in no check-in at an
 * in-person property: pending, with no due time (ADR-0146).
 */
export function isAwaitingCheckIn(
  filing: Pick<FilingLike, "dueAt" | "status">
): boolean {
  return filing.status === "pending" && filing.dueAt === null;
}

function registrationState(
  governing: FilingLike,
  withdrawn: boolean
): GuestRegistrationState {
  if (withdrawn) {
    return "withdrawn";
  }
  if (governing.filedManuallyAt) {
    return "filed_manually";
  }
  if (isAwaitingCheckIn(governing)) {
    return "awaiting_check_in";
  }
  return STATE_BY_STATUS[governing.status];
}

/**
 * Reduce one guest's filings to the state shown against their name.
 *
 * Shared so the reservation drawer's chip and the AI assistant's answer cannot
 * disagree about whether a guest is registered.
 */
export function summarizeGuestFilings<T extends FilingLike>(
  filings: T[]
): GuestRegistrationSummary<T> | null {
  const governing = governingFiling(filings);
  if (!governing) {
    return null;
  }

  // Act on the SAME filing the chip reports: governing is already the
  // highest-attention filing (needs_host_action outranks all, tie-broken to
  // check-in), so a separate find() could point the action at the other
  // filing when both check-in and check-out need action with different errors.
  const actionable =
    governing.status === "needs_host_action" || governing.status === "failed"
      ? governing
      : null;

  const hasScheduledCheckout = filings.some(
    (f) => f.kind === "check_out" && f.status === "pending"
  );

  // The filing whose record is in the register. Kept even once withdrawn, so
  // the row can name the authority it was withdrawn from; whether to offer the
  // action is `filed` plus the state below.
  const accepted = (
    ACCEPTED_GUEST_FILING_STATUSES as readonly string[]
  ).includes(governing.status);
  // A withdrawal is the guest-stay's state, not one direction's: the register
  // keys its record by the stay, and both filings share it. So ANY withdrawn
  // filing makes the row withdrawn, whichever one `governingFiling` picked.
  // Without this, an arrival withdrawn beside a `submitted` departure reads as
  // "submitted" (it outranks `confirmed`) and the card offers the withdrawal a
  // second time, for a record already cancelled.
  //
  // The STATE is decided by the presence of a timestamp, never by whether it
  // parses. A record withdrawn at an unreadable time is still withdrawn, and
  // deciding otherwise would put "Registered" on a cancelled record, which is
  // the one reading that sends a host looking for it in the register.
  const withdrawn = filings.some((f) => Boolean(f.withdrawnAt));
  // The DATE is the LATEST, not whichever row the caller's query returned
  // first: both directions can be withdrawn independently, and the row should
  // name when the record was last taken out. An unparseable value is skipped
  // rather than kept — every comparison against an invalid date is false, so
  // keeping one would make it win every later comparison — and the note
  // renders without a date, which it already handles.
  const withdrawnAt = filings.reduce<Date | null>((latest, f) => {
    if (!f.withdrawnAt) {
      return latest;
    }
    const at = new Date(f.withdrawnAt);
    if (Number.isNaN(at.getTime())) {
      return latest;
    }
    return latest === null || at > latest ? at : latest;
  }, null);
  const filed = accepted ? governing : null;

  return {
    actionable: actionable ?? null,
    errorCode: actionable?.lastErrorCode ?? null,
    // A withdrawal outranks the status it sits on: the filing is still
    // `confirmed`, and saying "registered" for a record the host has taken out
    // of the register is the one reading that would send them looking for it.
    filed,
    hasScheduledCheckout,
    state: registrationState(governing, withdrawn),
    withdrawnAt,
  };
}

/**
 * Whether checking a guest in leaves this filing unsent (ADR-0146): an arrival
 * a register holds or held (a withdrawn guest is never filed again), or one
 * waiting on the host. The route's twin is `CHECK_IN_LEAVES_UNSENT` (api).
 */
export function checkInLeavesUnsent(
  filing: Omit<FilingLike, "dueAt">
): boolean {
  if (filing.kind !== "check_in") {
    return false;
  }
  return (
    filing.status === "needs_host_action" ||
    Boolean(filing.withdrawnAt) ||
    (FILED_GUEST_FILING_STATUSES as readonly string[]).includes(
      filing.status
    ) ||
    Boolean(filing.filedManuallyAt) ||
    filing.lastErrorCode === "unattributed"
  );
}
